import React, { useState, useContext, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { ApplicationContext } from "./ApplicationContext";
import { useLogout } from "./useLogout";

const NavbarComponent = () => {
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { userRole, userName, basketItems } = useContext(ApplicationContext);
  const logoutFunction = useLogout();
  const location = useLocation();

  // Close dropdowns when route changes
  useEffect(() => {
    setIsProfileOpen(false);
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isProfileOpen && !event.target.closest('.profile-dropdown')) {
        setIsProfileOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isProfileOpen]);

  const NavItem = ({ to, children }) => {
    const isActive = location.pathname === to;
    return (
      <NavLink
        to={to}
        className={`px-3 py-2 rounded-md text-sm font-medium transition-colors duration-200 ${
          isActive 
            ? 'bg-red-800 text-white' 
            : 'text-red-100 hover:bg-red-800 hover:text-white'
        }`}
      >
        {children}
      </NavLink>
    );
  };

  const renderNavItems = () => {
    const items = [];

    switch (userRole) {
      case "ROLE_ADMIN":
        items.push(
          <NavItem key="shop" to="/shop">Shop</NavItem>,
          <NavItem key="articles" to="/articles">Artikli</NavItem>,
          <NavItem key="clients" to="/clients">Klijenti</NavItem>,
          <NavItem key="brands" to="/brands">Brendovi</NavItem>,
          <NavItem key="indents" to="/indents">Porudžbine</NavItem>,
          <NavItem key="basket" to="/basket">
            <div className="flex items-center">
              <span>Korpa</span>
              {basketItems?.length > 0 && (
                <span className="ml-2 px-2 py-1 text-xs bg-red-600 text-white rounded-full">
                  {basketItems.length}
                </span>
              )}
            </div>
          </NavItem>
        );
        break;
      case "ROLE_USER":
        items.push(
          <NavItem key="shop" to="/shop">Shop</NavItem>,
          <NavItem key="indents" to="/indents">Porudžbine</NavItem>,
          <NavItem key="basket" to="/basket">
            <div className="flex items-center">
              <span>Korpa</span>
              {basketItems?.length > 0 && (
                <span className="ml-2 px-2 py-1 text-xs bg-red-600 text-white rounded-full">
                  {basketItems.length}
                </span>
              )}
            </div>
          </NavItem>
        );
        break;
      case "ROLE_FAKTURISTA":
        items.push(
          <NavItem key="clients" to="/clients">Klijenti</NavItem>,
          <NavItem key="indents" to="/indents">Porudžbine</NavItem>
        );
        break;
      case "ROLE_MAGACIONER":
        items.push(
          <NavItem key="indents" to="/indents">Porudžbine</NavItem>
        );
        break;
      case "ROLE_REGISTRATION":
        items.push(
          <NavItem key="registration" to="/userManualRegistration">Registracija Na Portal</NavItem>
        );
        break;
      default:
        break;
    }

    return items;
  };

  const ProfileDropdown = () => (
    <div className="relative profile-dropdown">
      <button
        onClick={() => setIsProfileOpen(!isProfileOpen)}
        className="flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium text-red-100 hover:bg-red-800 hover:text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-red-900 focus:ring-white"
      >
        <span>{userName}</span>
        <svg
          className={`h-5 w-5 transition-transform duration-200 ${isProfileOpen ? 'transform rotate-180' : ''}`}
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
        </svg>
      </button>

      {isProfileOpen && (
        <div 
          className="absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 py-1 z-50"
        >
          <Link
            to="/userInfo"
            className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
          >
            O profilu
          </Link>
          <Link
            to="/change-password"
            className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
          >
            Promeni Šifru
          </Link>
          <button
            onClick={logoutFunction}
            className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
          >
            Odjavi se
          </button>
        </div>
      )}
    </div>
  );

  const renderMobileNavItems = () => {
    const items = [];

    switch (userRole) {
      case "ROLE_ADMIN":
        items.push(
          <Link
            key="shop"
            to="/shop"
            className="block px-3 py-2 rounded-md text-base font-medium text-red-100 hover:bg-red-700 hover:text-white"
          >
            Shop
          </Link>,
          <Link
            key="articles"
            to="/articles"
            className="block px-3 py-2 rounded-md text-base font-medium text-red-100 hover:bg-red-700 hover:text-white"
          >
            Artikli
          </Link>,
          <Link
            key="clients"
            to="/clients"
            className="block px-3 py-2 rounded-md text-base font-medium text-red-100 hover:bg-red-700 hover:text-white"
          >
            Klijenti
          </Link>,
          <Link
            key="brands"
            to="/brands"
            className="block px-3 py-2 rounded-md text-base font-medium text-red-100 hover:bg-red-700 hover:text-white"
          >
            Brendovi
          </Link>,
          <Link
            key="indents"
            to="/indents"
            className="block px-3 py-2 rounded-md text-base font-medium text-red-100 hover:bg-red-700 hover:text-white"
          >
            Porudžbine
          </Link>,
          <Link
            key="basket"
            to="/basket"
            className="block px-3 py-2 rounded-md text-base font-medium text-red-100 hover:bg-red-700 hover:text-white"
          >
            <div className="flex items-center justify-between">
              <span>Korpa</span>
              {basketItems?.length > 0 && (
                <span className="px-2 py-1 text-xs bg-red-600 text-white rounded-full">
                  {basketItems.length}
                </span>
              )}
            </div>
          </Link>
        );
        break;

      case "ROLE_USER":
        items.push(
          <Link
            key="shop"
            to="/shop"
            className="block px-3 py-2 rounded-md text-base font-medium text-red-100 hover:bg-red-700 hover:text-white"
          >
            Shop
          </Link>,
          <Link
            key="indents"
            to="/indents"
            className="block px-3 py-2 rounded-md text-base font-medium text-red-100 hover:bg-red-700 hover:text-white"
          >
            Porudžbine
          </Link>,
          <Link
            key="basket"
            to="/basket"
            className="block px-3 py-2 rounded-md text-base font-medium text-red-100 hover:bg-red-700 hover:text-white"
          >
            <div className="flex items-center justify-between">
              <span>Korpa</span>
              {basketItems?.length > 0 && (
                <span className="px-2 py-1 text-xs bg-red-600 text-white rounded-full">
                  {basketItems.length}
                </span>
              )}
            </div>
          </Link>
        );
        break;

      case "ROLE_FAKTURISTA":
        items.push(
          <Link
            key="clients"
            to="/clients"
            className="block px-3 py-2 rounded-md text-base font-medium text-red-100 hover:bg-red-700 hover:text-white"
          >
            Klijenti
          </Link>,
          <Link
            key="indents"
            to="/indents"
            className="block px-3 py-2 rounded-md text-base font-medium text-red-100 hover:bg-red-700 hover:text-white"
          >
            Porudžbine
          </Link>
        );
        break;

      case "ROLE_MAGACIONER":
        items.push(
          <Link
            key="indents"
            to="/indents"
            className="block px-3 py-2 rounded-md text-base font-medium text-red-100 hover:bg-red-700 hover:text-white"
          >
            Porudžbine
          </Link>
        );
        break;

      case "ROLE_REGISTRATION":
        items.push(
          <Link
            key="registration"
            to="/userManualRegistration"
            className="block px-3 py-2 rounded-md text-base font-medium text-red-100 hover:bg-red-700 hover:text-white"
          >
            Registracija Na Portal
          </Link>
        );
        break;

      default:
        break;
    }

    return items;
  };

  return (
    <nav className="bg-red-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <div className="flex-shrink-0">
            <Link to="/" className="flex items-center text-white font-semibold text-lg">
              <svg className="h-8 w-8 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              Zar Mance Portal
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex md:items-center md:space-x-4">
            {renderNavItems()}
            <ProfileDropdown />
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="inline-flex items-center justify-center p-2 rounded-md text-red-100 hover:bg-red-800 hover:text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-red-900 focus:ring-white"
            >
              <span className="sr-only">Open main menu</span>
              {!isMobileMenuOpen ? (
                <svg className="block h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              ) : (
                <svg className="block h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden">
          <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
            {renderMobileNavItems()}
          </div>
          <div className="pt-4 pb-3 border-t border-red-700">
            <div className="flex items-center px-5">
              <div className="flex-shrink-0">
                <span className="text-red-100 text-base font-medium">
                  {userName}
                </span>
              </div>
            </div>
            <div className="mt-3 px-2 space-y-1">
              <Link
                to="/userInfo"
                className="block px-3 py-2 rounded-md text-base font-medium text-red-100 hover:bg-red-700 hover:text-white"
              >
                O profilu
              </Link>
              <Link
                to="/change-password"
                className="block px-3 py-2 rounded-md text-base font-medium text-red-100 hover:bg-red-700 hover:text-white"
              >
                Promeni Šifru
              </Link>
              <button
                onClick={logoutFunction}
                className="block w-full text-left px-3 py-2 rounded-md text-base font-medium text-red-100 hover:bg-red-700 hover:text-white"
              >
                Odjavi se
              </button>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default NavbarComponent;
